import { useEffect, useRef, useState, useCallback } from 'react';
import { listAlerts } from '../services/api';

// useAlertsWebSocket — live unread-alert-count for the navbar badge.
//
// Replaces the old 60s `setInterval` poll in Navbar.js with a WebSocket
// push from the backend (`/api/v1/ws/alerts`). The matcher broadcasts a
// fresh unread count whenever it creates new Alert rows, so the badge
// updates in near-real-time instead of lagging up to a minute.
//
// Degraded mode is a hard requirement, not a nice-to-have: if the WS
// can't be opened (proxy strips Upgrade, server old, network flaky)
// the hook falls back to the exact same 60s `listAlerts` poll the
// navbar used before. So the worst case is "no regression", never
// "no badge".
//
// Behavior:
//   - enabled=false (e.g. on /login) -> does nothing, count stays 0.
//   - opens ws://.../ws/alerts with the jwt from localStorage in the
//     Sec-WebSocket-Protocol header, never in the URL (land-ai#1679).
//   - on message: updates the live unread count.
//   - on close/error: reconnects with exponential backoff (capped),
//     and meanwhile runs the 60s poll so the badge stays warm.
//   - once a reconnect succeeds, the poll is torn down again.
//   - cleans up the socket, timers, and listeners on unmount.

const POLL_INTERVAL_MS = 60000;
const BASE_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30000;
// Give up trying the WS after this many consecutive failed attempts
// and just stay on the poll — avoids hammering a server that plainly
// doesn't speak WebSocket.
const MAX_WS_ATTEMPTS = 5;
// Stale-connection watchdog: the server sends `{"type":"ping"}` every
// 25s. If we go this many ms without ANY frame, the socket is assumed
// dead — close it (which triggers `onclose` and a reconnect) so the
// badge doesn't silently freeze on a half-closed TCP. The classic
// failure is a browser that goes to sleep, the OS keeps the TCP socket
// alive briefly, then the network path dies without the WebSocket's
// `onclose` ever firing on the client. 75000ms gives us 3 missed
// pings before we tear down — plenty of grace for a transient stall
// but tight enough that the navbar recovers within ~1 poll cycle.
const STALE_PING_TIMEOUT_MS = 75000;

// Mirror api.js DEFAULT_API_BASE: a BARE "/api/v1". The reverse-proxy sub-path
// prefix ("/land-ai") is added by the SERVING layer, not the bundle — on the
// public deploy an upstream sub_filter rewrites "/api/v1" -> "/land-ai/api/v1"
// in the served JS, so this string goes out as "/land-ai/api/v1/ws/alerts".
// Do NOT prepend getBasePath(): that double-applies the prefix on public
// ("/land-ai" + sub_filter'd "/land-ai/api/v1") and the WS 404/403s in a loop.
const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

// land-ai#1679: a browser cannot set Authorization on a handshake, and a JWT in
// the query string is written to the access log of every hop (uvicorn, nginx)
// and into nginx's error_log, where no filter reaches it. The JWT therefore
// rides in the Sec-WebSocket-Protocol request header, which none of them log:
//   new WebSocket(url, ['landai.bearer', '<jwt>'])
// The server echoes 'landai.bearer' (never the token) to accept. The label
// must equal WS_BEARER_SUBPROTOCOL in backend/src/landai/api/v1/auth.py;
// backend/tests/test_1679_ws_token_out_of_url.py fails if the two differ.
export const ALERTS_WS_AUTH_PROTOCOL = 'landai.bearer';

// A subprotocol must be an RFC 7230 `token` (RFC 6455 section 4.1; the
// WebSocket constructor throws a SyntaxError otherwise). A JWT is base64url
// segments joined by dots, which is inside this set; base64 padding ("=") and
// "/" are not, and the app does not mint them.
const SUBPROTOCOL_TOKEN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

// The Sec-WebSocket-Protocol list that carries `token`, or null when the token
// cannot be sent that way. Null means "do not open the socket": the
// constructor's SyntaxError message names the offending subprotocol, i.e. the
// token, so it is never allowed to be thrown (or reported) in the first place.
export function buildAlertsWsProtocols(token) {
  if (typeof token !== 'string' || !SUBPROTOCOL_TOKEN.test(token)) return null;
  return [ALERTS_WS_AUTH_PROTOCOL, token];
}

// Build the absolute ws(s):// URL for the alerts endpoint from the
// (possibly relative) API base. A relative base like "/api/v1" is
// resolved against window.location; an absolute "http(s)://..." base
// is rewritten to its ws(s) equivalent. It carries no credential.
export function buildAlertsWsUrl() {
  let origin = '';
  let path = API_BASE;
  if (/^https?:\/\//i.test(API_BASE)) {
    const u = new URL(API_BASE);
    origin = u.origin;
    path = u.pathname.replace(/\/$/, '');
  } else if (typeof window !== 'undefined' && window.location) {
    origin = window.location.origin;
  }
  const wsOrigin = origin.replace(/^http/i, 'ws');
  return `${wsOrigin}${path}/ws/alerts`;
}

// `authKey` is any value that changes when the logged-in identity
// changes (e.g. the user id, or the user's email). Passing it lets the
// hook tear down and re-establish the connection on login/logout so a
// freshly authenticated user gets their own count without a page
// refresh — mirroring the old Navbar poll's `[user]` effect dep.
export default function useAlertsWebSocket(enabled = true, authKey = null) {
  const [unreadCount, setUnreadCount] = useState(0);

  // Mutable refs so the reconnect/poll machinery doesn't churn the
  // effect on every render.
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);
  // Watchdog: armed whenever a frame arrives, fires if no frame
  // arrives within STALE_PING_TIMEOUT_MS. Each rearm replaces the
  // previous timer so we never accumulate them.
  const staleTimerRef = useRef(null);

  // --- 60s poll (degraded mode) ------------------------------------
  const fetchUnreadOnce = useCallback(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    listAlerts({ per_page: 1 })
      .then((data) => {
        if (!cancelledRef.current) setUnreadCount(data.unread_count || 0);
      })
      .catch(() => {});
  }, []);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current != null) return; // already polling
    fetchUnreadOnce();
    pollTimerRef.current = setInterval(fetchUnreadOnce, POLL_INTERVAL_MS);
  }, [fetchUnreadOnce]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setUnreadCount(0);
      return undefined;
    }

    cancelledRef.current = false;
    // Fresh identity / fresh mount -> fresh backoff budget.
    attemptsRef.current = 0;

    const connect = () => {
      if (cancelledRef.current) return;

      const token =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('auth_token')
          : null;

      // No WebSocket support in this environment, or we've exhausted
      // our WS attempts, or we have no JWT to authenticate with (or one
      // that cannot travel as a subprotocol) -> permanent degraded mode.
      // The token check is essential: without it the hook opens the
      // socket with no credential, the backend rejects with close code
      // 1008/403, onclose schedules a retry, and we generate a
      // 5-attempt storm per mount. Multiple tabs + each navbar
      // instance multiply that into ~250 backend 403s/hour.
      // Polling (listAlerts) is auth'd via the standard Authorization
      // header and degrades cleanly when there's no token.
      const protocols = buildAlertsWsProtocols(token);
      if (
        typeof WebSocket === 'undefined' ||
        attemptsRef.current >= MAX_WS_ATTEMPTS ||
        !protocols
      ) {
        startPolling();
        return;
      }

      let ws;
      try {
        ws = new WebSocket(buildAlertsWsUrl(), protocols);
      } catch (e) {
        // Constructor threw (blocked) -> degrade and back off. `e` is
        // deliberately not logged or reported: for a bad subprotocol its
        // message quotes the token (land-ai#1679).
        attemptsRef.current += 1;
        startPolling();
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      // Arm the stale-frame watchdog. Called on `onopen` and on every
      // inbound frame. If the timer fires, the socket is considered
      // dead and force-closed — `onclose` then handles the reconnect.
      const armStaleWatchdog = () => {
        if (staleTimerRef.current != null) {
          clearTimeout(staleTimerRef.current);
        }
        staleTimerRef.current = setTimeout(() => {
          staleTimerRef.current = null;
          if (cancelledRef.current) return;
          const sock = wsRef.current;
          if (!sock) return;
          // Force-close: the browser fires `onclose` synchronously
          // for our reconnect path. Use 4000-4999 (app-defined) and
          // a short reason so server-side logs are searchable.
          try {
            sock.close(4000, 'stale_ping_watchdog');
          } catch (e) {
            /* already closing */
          }
        }, STALE_PING_TIMEOUT_MS);
      };

      ws.onopen = () => {
        // Live channel is up — reset backoff and tear down the poll.
        attemptsRef.current = 0;
        stopPolling();
        armStaleWatchdog();
      };

      ws.onmessage = (event) => {
        if (cancelledRef.current) return;
        // Any frame at all — count or ping — proves the socket is
        // still alive end-to-end, so rearm the watchdog before we do
        // anything else with the payload.
        armStaleWatchdog();
        try {
          const msg = JSON.parse(event.data);
          if (!msg || typeof msg !== 'object') return;
          // Server-driven keepalive ping (issue #52 stability fix).
          // The JS WebSocket API exposes no protocol-level ping/pong,
          // so the backend sends `{"type":"ping"}` on a 25s cadence
          // to prevent proxy/NAT idle reapers from killing the
          // socket. Nothing for the client to do beyond receiving
          // the frame (which keeps Node's HPM proxy and any L4 LB
          // marking the connection as alive).
          if (msg.type === 'ping') return;
          if (typeof msg.unread_count === 'number') {
            setUnreadCount(msg.unread_count);
          }
        } catch (e) {
          // Ignore unparseable frames.
        }
      };

      ws.onerror = () => {
        // onclose fires right after onerror; let onclose drive the
        // reconnect so we don't double-schedule.
      };

      ws.onclose = () => {
        wsRef.current = null;
        // Disarm the watchdog — the socket is already gone.
        if (staleTimerRef.current != null) {
          clearTimeout(staleTimerRef.current);
          staleTimerRef.current = null;
        }
        if (cancelledRef.current) return;
        attemptsRef.current += 1;
        // Keep the badge warm with the poll while we retry.
        startPolling();
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelledRef.current) return;
      if (reconnectTimerRef.current != null) return;
      if (attemptsRef.current >= MAX_WS_ATTEMPTS) return; // stay on poll
      const delay = Math.min(
        MAX_RECONNECT_MS,
        BASE_RECONNECT_MS * 2 ** (attemptsRef.current - 1),
      );
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    // Re-fetch immediately when the tab becomes visible again so the
    // badge is fresh, regardless of WS vs poll mode.
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        // If we're polling, refresh now. If the WS is open the server
        // pushes, but a poll-refresh here is harmless and covers the
        // case where the socket silently died.
        fetchUnreadOnce();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    connect();

    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current != null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (staleTimerRef.current != null) {
        clearTimeout(staleTimerRef.current);
        staleTimerRef.current = null;
      }
      stopPolling();
      if (wsRef.current) {
        // Detach handlers first so the close we trigger doesn't kick
        // off a reconnect.
        const ws = wsRef.current;
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        try {
          ws.close();
        } catch (e) {
          /* already closing */
        }
        wsRef.current = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    // authKey is intentionally in the dep list: a login/logout should
    // reset attempt counters and reconnect with the new token.
  }, [enabled, authKey, startPolling, stopPolling, fetchUnreadOnce]);

  return unreadCount;
}
